import { useCallback, useMemo } from 'react'
import store, { dispatch } from 'store'
import { translate } from 'utils/translations'
import { downloadMedia } from 'utils'
import { T } from 'consts/translationKeys'
import { SUB_SIDEBAR_KEYS } from 'consts/sidebar'
import { IAIVideoProject, ReferenceMediaImageType } from 'types/aiVideo'
import { useAddAiItemToTimeline } from 'hooks/useAddAiItemToTimeline'
import { editorThunks } from 'store/features/editor/thunks'
import { aiVideoThunks } from 'store/features/aiVideo/thunks'
import { aiAvatarThunks } from 'store/features/aiAvatar/thunks'
import { projectDataSelectors } from 'store/features/projectData/selectors'
import { hasAiAvatarMarker } from 'utils/media'
import {
  ReferenceMode,
  TabKey
} from 'containers/SubSidebar/AIVideoContent/consts'
import { createReferenceMediaItem } from 'store/features/aiVideo/utils'
import { convertAiVideoToBaseMediaItem } from 'containers/SubSidebar/AIVideoContent/HistoryTab/utils'
import { IFlyoutMenuItem } from 'common-components/ActionsFlyout/types'
import { IOverlayActionsData } from 'common-components/ActionsOverlay/types'
import DownloadIcon from 'assets/svgr-icons/Download.svg'
import PlusIcon from 'assets/svgr-icons/Plus.svg'
import RegenerateIcon from 'assets/svgr-icons/Regenerate.svg'

const MENU_ICON_SIZE = { width: 16, height: 16 }

const menuIcons = {
  add: <PlusIcon />,
  regenerate: <RegenerateIcon />,
  download: <DownloadIcon />
} as const

interface IUseAiLibVideoActionsProps {
  video: IAIVideoProject
  onClosePreview?: () => void
}

interface IUseAiLibVideoActionsReturn extends IOverlayActionsData {
  handleAddToTimeline: () => void
  handleRegenerate: () => void
  handleUseAsReference: () => void
  handleDownload: () => void
  isAiGenerated: boolean
}

export const useAiLibVideoActions = ({
  video,
  onClosePreview
}: IUseAiLibVideoActionsProps): IUseAiLibVideoActionsReturn => {
  const addItemToTimeline = useAddAiItemToTimeline()
  const isAiGenerated = !!video.prompt || !!video.imageUrl
  // Avatars share the ai-video table/API; `meta.aiAvatarProjectId` is the
  // discriminator (see `hasAiAvatarMarker`). It routes Re-Generate to the AI
  // Avatar tab/flow instead of AI Video.
  const isAvatar = hasAiAvatarMarker(video.meta)

  const baseMediaItem = useMemo(
    () => convertAiVideoToBaseMediaItem(video),
    [video]
  )

  const handleAddToTimeline = useCallback(() => {
    addItemToTimeline(baseMediaItem)
    onClosePreview?.()
  }, [baseMediaItem, addItemToTimeline, onClosePreview])

  const handleRegenerate = useCallback(async () => {
    onClosePreview?.()

    if (isAvatar) {
      // Fresh-generation mode: useAvatarGenerate treats a selected finished
      // avatar clip as an in-place regen, so clear the selection to force a new
      // clip (mirrors AI Video's history Re-Generate). Prefill by asset id since
      // the history clip generally isn't on the timeline.
      dispatch(editorThunks.setActiveTimelineItems([]))
      dispatch(editorThunks.setActiveSidebarTab(SUB_SIDEBAR_KEYS.AI_AVATAR))
      await dispatch(aiAvatarThunks.setGenerationParamsByAssetId(video.id))
      return
    }

    const mediaItems = projectDataSelectors.mediaItems(store.getState())
    const matchingItem = mediaItems.find(
      item => String(item.assetId) === String(video.id)
    )

    if (matchingItem) {
      dispatch(
        editorThunks.setActiveTimelineItems([
          { id: matchingItem.id, type: matchingItem.type }
        ])
      )
    } else {
      dispatch(editorThunks.setActiveTimelineItems([]))
    }

    dispatch(editorThunks.setActiveSidebarTab(SUB_SIDEBAR_KEYS.AI_VIDEO))
    await dispatch(aiVideoThunks.setGenerationParamsByAssetId(video.id))

    if (!video.imageUrl) {
      const refImageUrl = video.referenceMedias?.images?.[0]?.url
      if (refImageUrl) {
        dispatch(
          aiVideoThunks.addReferenceMediaItem({
            item: createReferenceMediaItem(
              ReferenceMediaImageType.FIRST_FRAME,
              refImageUrl
            ),
            bucket: ReferenceMode.FRAMES
          })
        )
      }
    }

    dispatch(aiVideoThunks.setActiveTab(TabKey.GENERATE))
    dispatch(aiVideoThunks.setShowVersionHistoryTab(true))
  }, [
    isAvatar,
    video.id,
    video.imageUrl,
    video.referenceMedias,
    onClosePreview
  ])

  const handleUseAsReference = useCallback(() => {
    const refImageUrl =
      video.imageUrl ?? video.referenceMedias?.images?.[0]?.url
    if (!refImageUrl) return

    onClosePreview?.()
    dispatch(editorThunks.setActiveSidebarTab(SUB_SIDEBAR_KEYS.AI_VIDEO))
    dispatch(aiVideoThunks.clearGenerationParams())
    dispatch(
      aiVideoThunks.addReferenceMediaItem({
        item: createReferenceMediaItem(
          ReferenceMediaImageType.FIRST_FRAME,
          refImageUrl
        ),
        bucket: ReferenceMode.FRAMES
      })
    )
    dispatch(aiVideoThunks.setActiveTab(TabKey.GENERATE))
  }, [video.imageUrl, video.referenceMedias, onClosePreview])

  const handleDownload = useCallback(async () => {
    if (!video?.videoUrl) return
    await downloadMedia({
      filePath: video.videoUrl,
      fileName: `ai-video-${video.id}`
    })
  }, [video.videoUrl, video.id])

  const secondaryActions = useMemo<IFlyoutMenuItem[]>(
    () => [
      ...(isAiGenerated
        ? [
            {
              key: T.RE_GENERATE,
              title: translate(T.RE_GENERATE),
              icon: menuIcons.regenerate,
              onClick: handleRegenerate,
              iconSize: MENU_ICON_SIZE
            }
          ]
        : []),
      {
        key: T.DOWNLOAD,
        title: translate(T.DOWNLOAD),
        icon: menuIcons.download,
        onClick: handleDownload,
        iconSize: MENU_ICON_SIZE
      }
    ],
    [isAiGenerated, handleRegenerate, handleDownload]
  )

  return {
    secondaryActions:
      secondaryActions as IOverlayActionsData['secondaryActions'],
    handleAddToTimeline,
    handleRegenerate,
    handleUseAsReference,
    handleDownload,
    isAiGenerated
  }
}
